import type { RefObject } from 'react';

/**
 * Finds the nearest scrollable parent element
 * @param element - The element to start searching from
 * @returns The nearest scrollable parent or null
 */
export const getScrollParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = getComputedStyle(parent);
    if (/(auto|scroll)/.test(overflow + overflowY)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

/**
 * Scrolls to the first field with a validation error (in DOM order)
 * @param formRef - Reference to the form container element
 */
export const scrollToFirstError = (formRef: RefObject<HTMLDivElement | null>): void => {
  if (!formRef.current) return;

  // Use setTimeout to ensure validation styles have been applied
  setTimeout(() => {
    // Find elements with validation-error class (applied by Input/TextArea/etc components)
    const errorWrapper = formRef.current?.querySelector('[class*="validation-error"]');

    if (errorWrapper instanceof HTMLElement) {
      // Find visible input inside (skip hidden inputs used by dropdowns)
      const inputElement = errorWrapper.querySelector(
        'input:not([type="hidden"]), textarea, select'
      );
      // Use the wrapper itself for scrolling (it's always visible), input for focus
      const scrollParent = getScrollParent(errorWrapper);

      if (scrollParent) {
        const elementRect = errorWrapper.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();
        const scrollTop =
          scrollParent.scrollTop + elementRect.top - parentRect.top - parentRect.height / 2;
        scrollParent.scrollTo({ top: scrollTop, behavior: 'smooth' });
      } else {
        errorWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Focus the visible input if found
      if (inputElement instanceof HTMLElement) {
        inputElement.focus({ preventScroll: true });
      }
    }
  }, 100);
};
